import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ServiceType, ServiceCategory } from '../../../../models/ServiceType';
import { ServiceTypeService } from '../../../../services/service-type.service';
import { TableAction } from '../../../../shared/components/table/table.component';
import { StatusService } from '../../../../services/status.service';
import { Status } from '../../../../models/Status';

@Component({
  selector: 'app-services-list',
  templateUrl: './services-list.component.html',
  styleUrls: ['./services-list.component.css']
})
export class ServicesListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // ===== DADOS =====
  services: ServiceType[] = [];
  filteredServices: ServiceType[] = [];
  isLoading = false;
  error: string | null = null;
  statuses: Status[] = [];
  showToggleStatusModal = false;
  selectedServiceForToggle: any | null = null;
  isSubmittingToggle = false;

  // ===== PAGINAÇÃO =====
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // ===== FILTROS =====
  filters: { category: string; status: string } = {
    category: '',
    status: ''
  };

  categories = [
    { value: ServiceCategory.PREVENTIVA, label: 'Manutenção Preventiva' },
    { value: ServiceCategory.CORRETIVA, label: 'Manutenção Corretiva' },
    { value: ServiceCategory.ESTETICA, label: 'Estética' }
  ];

  // ===== CONFIGURAÇÃO DA TABELA =====
  tableTitle = 'Tipos de Serviços';
  showSearch = true;
  searchPlaceholder = 'Buscar por nome, código, categoria...';
  clickable = true;

  columns = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Código', label: 'code' },
    { titulo: 'Categoria', label: 'category' },
    { titulo: 'Tempo Estimado', label: 'estimatedTimeMinutes' },
    { titulo: 'Custo Atual', label: 'currentCost' },
    { titulo: 'Margem (%)', label: 'profitMarginPercentage' },
    { titulo: 'Preço Final', label: 'finalPrice' },
    { titulo: 'Status', label: 'status' }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Ativar/Desativar',
      icon: 'fas fa-toggle-on',
      action: 'toggle',
      class: 'btn-toggle'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  constructor(
    private serviceTypeService: ServiceTypeService,
    private statusService: StatusService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadStatuses();
    this.loadServices();
  }

  loadStatuses(): void {
    this.statusService.getByEntity('ServiceType').subscribe({
      next: (statuses: Status[]) => {
        this.statuses = statuses;
      },
      error: (err: any) => {
        console.error('Erro ao carregar status:', err);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== CARREGAMENTO DE DADOS =====

  /**
   * Carrega todos os serviços
   */
  loadServices(): void {
    this.isLoading = true;
    this.error = null;

    this.serviceTypeService.getAll({ page: this.currentPage, pageSize: this.pageSize })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.services = result.items.map((service: ServiceType) => {
            // Calcular preço final
            const finalPrice = this.calculateFinalPrice(service);

            return {
              ...service,
              code: service.code || '-',
              category: service.category || '-',
              estimatedTimeMinutes: this.formatTime(service.estimatedTimeMinutes),
              currentCost: service.currentCost ? `R$ ${service.currentCost.toFixed(2)}` : '-',
              profitMarginPercentage: service.profitMarginPercentage ? `${service.profitMarginPercentage}%` : '-',
              finalPrice: finalPrice ? `R$ ${finalPrice.toFixed(2)}` : '-',
              isActive: service.isActive,
              status: service.status || (service.isActive ? 'Ativo' : 'Inativo')
            } as any;
          });

          // Atualizar informações de paginação
          this.totalItems = result.totalItems;
          this.totalPages = result.totalPages;
          this.hasNextPage = this.currentPage < result.totalPages;
          this.hasPreviousPage = this.currentPage > 1;

          this.applyFilters();
          this.isLoading = false;
        },
        error: (error) => {
          this.error = 'Erro ao carregar serviços. Tente novamente.';
          this.isLoading = false;
          console.error('Erro ao carregar serviços:', error);
        }
      });
  }

  /**
   * Recarrega os dados
   */
  retryLoad(): void {
    this.loadServices();
  }

  // ===== FILTROS =====

  /**
   * Aplica filtros aos dados
   */
  applyFilters(): void {
    let filtered = [...this.services];

    // Filtro de categoria
    if (this.filters.category) {
      filtered = filtered.filter((service: any) =>
        service.category && service.category.toLowerCase().includes(this.filters.category.toLowerCase())
      );
    }

    // Filtro de status
    if (this.filters.status) {
      filtered = filtered.filter((service: any) =>
        service.status === this.filters.status
      );
    }

    this.filteredServices = filtered;
  }

  /**
   * Limpa todos os filtros
   */
  clearFilters(): void {
    this.filters = {
      category: '',
      status: ''
    };
    this.applyFilters();
  }

  // ===== AÇÕES DA TABELA =====

  /**
   * Manipula cliques nas ações da tabela
   */
  onActionClick(event: { action: string; item: any }): void {
    const { action, item } = event;

    switch (action) {
      case 'view':
        this.viewService(item.id);
        break;
      case 'edit':
        this.editService(item.id);
        break;
      case 'toggle':
        this.toggleStatus(item);
        break;
      case 'delete':
        this.deleteService(item);
        break;
      default:
        break;
    }
  }

  // ===== NAVEGAÇÃO =====

  /**
   * Navega para criação de novo serviço
   */
  createService(): void {
    this.router.navigate(['/operations/services/new']);
  }

  /**
   * Navega para detalhes do serviço
   */
  viewService(serviceId: number): void {
    this.router.navigate(['/operations/services', serviceId]);
  }

  /**
   * Navega para edição do serviço
   */
  editService(serviceId: number): void {
    this.router.navigate(['/operations/services', serviceId, 'edit']);
  }

  /**
   * Alterna status do serviço
   */
  toggleStatus(service: any): void {
    this.selectedServiceForToggle = service;
    this.showToggleStatusModal = true;
  }

  closeToggleStatusModal(): void {
    this.showToggleStatusModal = false;
    this.selectedServiceForToggle = null;
    this.isSubmittingToggle = false;
  }

  confirmToggleStatus(): void {
    if (!this.selectedServiceForToggle?.id || this.isSubmittingToggle) {
      return;
    }

    this.isSubmittingToggle = true;
    this.serviceTypeService.toggleStatus(this.selectedServiceForToggle.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeToggleStatusModal();
          this.loadServices();
        },
        error: (error) => {
          this.error = 'Erro ao alterar status do serviço';
          this.isSubmittingToggle = false;
          console.error('Erro:', error);
        }
      });
  }

  get isSelectedServiceActive(): boolean {
    return Boolean(this.selectedServiceForToggle?.isActive);
  }

  /**
   * Exclui serviço
   */
  deleteService(service: any): void {
    if (!confirm(`Deseja realmente excluir o serviço "${service.name}"?`)) {
      return;
    }

    this.serviceTypeService.delete(service.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadServices();
        },
        error: (error) => {
          this.error = 'Erro ao excluir serviço';
          console.error('Erro:', error);
        }
      });
  }

  // ===== FORMATADORES =====

  /**
   * Calcula preço final com margem
   */
  calculateFinalPrice(service: any): number {
    if (!service.currentCost || !service.profitMarginPercentage) {
      return service.currentCost || 0;
    }
    const margin = service.profitMarginPercentage / 100;
    return service.currentCost + (service.currentCost * margin);
  }

  /**
   * Formata tempo para exibição
   */
  formatTime(minutes?: number): string {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.filteredServices = [...this.filteredServices].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  // ===== CONTROLES DE PAGINAÇÃO =====

  /**
   * Muda para próxima página
   */
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadServices();
    }
  }

  /**
   * Altera o tamanho da página
   */
  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadServices();
  }
}
